import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Home from './pages/home';
import About from './pages/about';
import Contact from './pages/contact';
import Shop from './pages/shop';
import Cart from './pages/cart';
import Header from './components/Header';
import Layout from './layouts/layout';
import Footer from './components/Footer';
import ProductPage from './pages/product';
import Legal from './pages/legal';
import ScrollToTop from './functions/ScrollToTop';
import { CartProvider } from './context/CartContext';
import { HelmetProvider } from 'react-helmet-async';
import CategoryPageWrapper from './pages/categories/[category]';
import { SpeedInsights } from '@vercel/speed-insights/react';
import Success from './pages/success';
import { UIProvider } from './context/UIContext';
import MetaPixel from './components/MetaPixel';
import NotFound from './pages/NotFound';
import ProductFeed from './pages/ProductFeed';
import { StockProvider } from './context/StockContext';
import { ProductsProvider } from './context/ProductsContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import CookieBanner from './components/CookieBanner';
import AnalyticsWrapper from './components/AnalyticsWrapper';
import { useCookieConsent } from './context/CookieConsentContext';
import { MediaProvider } from './context/MediaContext';
import { AuthProvider } from './context/AuthContext';
import { CMSLogin } from './pages/CMSLogin';
import { CMSDashboard } from './pages/CMSDashboard';
import { CMSProducts } from './pages/CMSProducts';
import { CMSInventory } from './pages/CMSInventory';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CMSAIAssistant } from './pages/CMSAIAssistant';
import { CMSMedia } from './pages/CMSMedia';
import { CMSTasks } from './pages/CMSTasks';
import { LocaleProvider } from './context/LocaleContext';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from './i18n/config';
import { getRoutePattern, ROUTE_DEFINITIONS } from './i18n/routes';
import { captureUtmParams } from './utils/utm';
import PrerenderSignal from './components/PrerenderSignal';

// Capture UTM params from landing URL on first render
captureUtmParams();

/** Map route keys to their page components */
const ROUTE_COMPONENTS: Record<string, React.ReactNode> = {
  home: <Home />,
  shop: <Shop />,
  about: <About />,
  contact: <Contact />,
  cart: <Cart />,
  product: <ProductPage />,
  categories: <CategoryPageWrapper />,
  legal: <Legal />,
  success: <Success />,
};

const App: React.FC = () => {
  return (
    <Router>
      <CookieConsentProvider>
        <HelmetProvider>
          <AuthProvider>
            <ProductsProvider>
              <MediaProvider>
                <StockProvider>
                  <CartProvider>
                    <UIProvider>
                      <MetaPixel />
                      <PrerenderSignal />
                      <ScrollToTop />
                      <Routes>
                        {/* CMS Routes (no header/footer, no locale) */}
                        <Route path="/cms/login" element={<CMSLogin />} />
                        <Route
                          path="/cms/dashboard"
                          element={
                            <ProtectedRoute>
                              <CMSDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cms/products"
                          element={
                            <ProtectedRoute>
                              <CMSProducts />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cms/inventory"
                          element={
                            <ProtectedRoute>
                              <CMSInventory />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cms/media"
                          element={
                            <ProtectedRoute>
                              <CMSMedia />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cms/tasks"
                          element={
                            <ProtectedRoute>
                              <CMSTasks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/cms/ai-assistant"
                          element={
                            <ProtectedRoute>
                              <CMSAIAssistant />
                            </ProtectedRoute>
                          }
                        />

                        {/* Public Routes (with header/footer + locale) */}
                        <Route
                          path="*"
                          element={
                            <LocaleProvider>
                              <div className="flex flex-col min-h-screen [overflow-x:clip]">
                                <Header />
                                <Layout>
                                  <Routes>
                                    {/* English (default) routes */}
                                    {ROUTE_DEFINITIONS.map(({ key, path }) => (
                                      <Route
                                        key={`en-${key}`}
                                        path={path}
                                        element={ROUTE_COMPONENTS[key]}
                                      />
                                    ))}

                                    {/* Non-default locale routes */}
                                    {SUPPORTED_LOCALES
                                      .filter((l) => l !== DEFAULT_LOCALE)
                                      .flatMap((locale) =>
                                        ROUTE_DEFINITIONS.map(({ key, path }) => (
                                          <Route
                                            key={`${locale}-${key}`}
                                            path={getRoutePattern(locale, path)}
                                            element={ROUTE_COMPONENTS[key]}
                                          />
                                        ))
                                      )}

                                    {/* Static routes (no translation) */}
                                    <Route path="/product-feed" element={<ProductFeed />} />
                                    <Route path="/google-feed" element={<ProductFeed />} />
                                    <Route path="*" element={<NotFound />} />
                                  </Routes>
                                </Layout>
                                <Footer />
                                <AnalyticsWrapper />
                                <CookieBanner />
                              </div>
                            </LocaleProvider>
                          }
                        />
                      </Routes>
                    </UIProvider>
                  </CartProvider>
                </StockProvider>
              </MediaProvider>
            </ProductsProvider>
          </AuthProvider>
        </HelmetProvider>
      </CookieConsentProvider>
    </Router>
  );
};

export default App;
